import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAward,
  faCircleCheck,
  faCircleExclamation,
  faMagnifyingGlass,
  faXmark,
  faTriangleExclamation,
  faRotate,
  faArrowUpRightFromSquare,
  faUser,
  faForwardStep,
  faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import './MyCertificates.css';

const API = process.env.REACT_APP_API_BASE_URL;

const FILTERS = [
  ['needed', 'Needed'],
  ['attention', 'Need attention'],
  ['added', 'Added'],
  ['all', 'All'],
];

// [1,2,3,5,7,8] -> "1–3, 5, 7–8"
const ranges = (nums) => {
  const out = [];
  for (let i = 0; i < nums.length; i += 1) {
    let j = i;
    while (j + 1 < nums.length && nums[j + 1] === nums[j] + 1) j += 1;
    out.push(i === j ? `${nums[i]}` : `${nums[i]}–${nums[j]}`);
    i = j;
  }
  return out.join(', ');
};
const shortDate = (s) => {
  if (!s) return '';
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const isAdded = (c) => Number(c.CertificateAdded) === 1;

const Certificates = () => {
  const [params, setParams] = useSearchParams();
  const filter = FILTERS.some(([k]) => k === params.get('filter')) ? params.get('filter') : 'needed';
  const [certificates, setCertificates] = useState([]);
  const [attention, setAttention] = useState([]);
  const [attentionError, setAttentionError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [query, setQuery] = useState('');

  // ---------- Load (same endpoint) ----------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('userData')}` };
        const [response, needs] = await Promise.all([
          axios.get(`${API}/api/admin/mystudents/certificatesneeded`, { headers }),
          // "Need attention" is extra: if it fails, the rest of the page still works
          axios.get(`${API}/api/admin/mystudents/certificatesattention`, { headers }).catch((err) => ({ failed: err })),
        ]);
        if (cancelled) return;
        setCertificates(response.data?.certificatesNeeded || []);
        setAttention(needs.failed ? [] : needs.data?.needAttention || []);
        setAttentionError(needs.failed ? 'We couldn’t load the students that need attention.' : '');
      } catch (err) {
        console.error('Error fetching certificates:', err);
        if (!cancelled) setError(err.response?.data?.message || 'We couldn’t load the certificates.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const setFilter = (key) => {
    const next = new URLSearchParams(params);
    if (key === 'needed') next.delete('filter');
    else next.set('filter', key);
    setParams(next, { replace: true });
  };

  const count = {
    needed: certificates.filter((c) => !isAdded(c)).length,
    attention: attention.length,
    added: certificates.filter(isAdded).length,
    all: certificates.length,
  };

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return certificates
      .filter((c) => (filter === 'needed' ? !isAdded(c) : filter === 'added' ? isAdded(c) : true))
      .filter((c) => !q || `${c.FirstName || ''} ${c.LastName || ''} ${c.ModuleName || ''}`.toLowerCase().includes(q))
      .sort(
        (a, b) =>
          Number(isAdded(a)) - Number(isAdded(b)) ||
          `${a.FirstName} ${a.LastName}`.localeCompare(`${b.FirstName} ${b.LastName}`) ||
          String(a.ModuleName || '').localeCompare(String(b.ModuleName || ''))
      );
  }, [certificates, filter, query]);

  const shownAttention = useMemo(() => {
    const q = query.trim().toLowerCase();
    return attention.filter((c) => !q || `${c.FirstName || ''} ${c.LastName || ''} ${c.ModuleName || ''} ${c.nextModule || ''}`.toLowerCase().includes(q));
  }, [attention, query]);

  // ---------- States ----------
  if (loading) {
    return (
      <div className="rmacr" aria-busy="true">
        <div className="rmacr-skel rmacr-skel--head" />
        <div className="rmacr-skel rmacr-skel--bar" />
        <div className="rmacr-grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="rmacr-skel" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rmacr">
        <div className="rmacr-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{error}</p>
          <button type="button" className="rmacr-btn" onClick={() => setReload((n) => n + 1)}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rmacr">
      <header className="rmacr-header">
        <div>
          <h1>Certificates</h1>
          <p>Students who finished a module, and whether their certificate is added.</p>
        </div>
        {(count.needed > 0 || count.attention > 0) && (
          <div className="rmacr-alerts">
            {count.needed > 0 && (
              <p className="rmacr-alert">
                <FontAwesomeIcon icon={faCircleExclamation} /> {count.needed} {count.needed === 1 ? 'certificate' : 'certificates'} to add
              </p>
            )}
            {count.attention > 0 && filter !== 'attention' && (
              <button type="button" className="rmacr-alert is-attention" onClick={() => setFilter('attention')}>
                <FontAwesomeIcon icon={faTriangleExclamation} /> {count.attention} need attention
              </button>
            )}
          </div>
        )}
      </header>

      <div className="rmacr-tools">
        <div className="rmacr-tabs" role="tablist" aria-label="Show">
          {FILTERS.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={filter === key} className={`is-${key} ${filter === key ? 'is-on' : ''}`} onClick={() => setFilter(key)}>
              {label} <span>{count[key]}</span>
            </button>
          ))}
        </div>
        <label className="rmacr-search">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <input type="search" placeholder="Search student or module" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search" />
          {query && (
            <button type="button" onClick={() => setQuery('')} aria-label="Clear search">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </label>
      </div>

      {filter === 'attention' ? (
        <>
          <p className="rmacr-hint">
            Modules that won’t reach the certificates list on their own: the student moved to a new module before every session was submitted, or
            skipped sessions and reached the end. Add the missing sessions as “Skipped class” in the student’s Submit tab and the module moves to
            Needed.
          </p>
          {attentionError ? (
            <div className="rmacr-empty">
              <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
              <p>{attentionError}</p>
              <button type="button" className="rmacr-btn" onClick={() => setReload((n) => n + 1)}>
                <FontAwesomeIcon icon={faRotate} /> Try again
              </button>
            </div>
          ) : shownAttention.length === 0 ? (
            <div className="rmacr-empty">
              <FontAwesomeIcon icon={query ? faAward : faCircleCheck} className={query ? '' : 'is-good'} />
              <p>{query ? 'No students match.' : 'Nothing needs attention.'}</p>
            </div>
          ) : (
            <ul className="rmacr-grid">
              {shownAttention.map((c) => {
                const skipped = c.reason === 'skipped';
                const pct = c.total ? Math.min(100, Math.round((c.done / c.total) * 100)) : 0;
                return (
                  <li key={`${c.UserId}-${c.ModuleId}`} className={`rmacr-card ${skipped ? 'is-skipped' : 'is-moved'}`}>
                    <div className="rmacr-card-top">
                      <span className="rmacr-icon">
                        <FontAwesomeIcon icon={skipped ? faForwardStep : faLayerGroup} />
                      </span>
                      <div className="rmacr-who">
                        <strong>
                          {c.FirstName} {c.LastName}
                        </strong>
                        <span>{c.ModuleName}</span>
                      </div>
                      <span className="rmacr-pill">{skipped ? 'Skipped sessions' : 'Moved on'}</span>
                    </div>
                    <p className="rmacr-why">
                      {skipped
                        ? `Reached the last session with ${c.missingCount} ${c.missingCount === 1 ? 'session' : 'sessions'} skipped.`
                        : `Started ${c.nextModule || 'another module'} with ${c.missingCount} ${c.missingCount === 1 ? 'session' : 'sessions'} not submitted.`}
                    </p>
                    <dl className="rmacr-facts">
                      {c.missing?.length > 0 && (
                        <div>
                          <dt>Missing</dt>
                          <dd>Session {ranges(c.missing)}</dd>
                        </div>
                      )}
                      {c.lastClass && (
                        <div>
                          <dt>Last class</dt>
                          <dd>{shortDate(c.lastClass)}</dd>
                        </div>
                      )}
                    </dl>
                    <div className="rmacr-progress">
                      <span className="rmacr-bar" aria-hidden="true">
                        <i style={{ width: `${pct}%` }} />
                      </span>
                      <small>
                        {c.done}/{c.total} submitted
                      </small>
                    </div>
                    <div className="rmacr-actions">
                      <a
                        className="rmacr-profile"
                        href={`/admin/StudentProfile/${c.UserId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open ${c.FirstName} ${c.LastName}'s profile`}
                        title="Student profile"
                      >
                        <FontAwesomeIcon icon={faUser} />
                      </a>
                      <a className="rmacr-open" href={`/admin/StudentProfile/${c.UserId}?tab=submitclasses`} target="_blank" rel="noopener noreferrer">
                        Fix in Submit tab <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                      </a>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : shown.length === 0 ? (
        <div className="rmacr-empty">
          <FontAwesomeIcon icon={filter === 'needed' && !query ? faCircleCheck : faAward} className={filter === 'needed' && !query ? 'is-good' : ''} />
          <p>{query ? 'No students match.' : filter === 'needed' ? 'All caught up — no certificates waiting.' : 'Nothing here yet.'}</p>
        </div>
      ) : (
        <ul className="rmacr-grid">
          {shown.map((c) => {
            const added = isAdded(c);
            const done = Number(c.UniqueSubmitClassCount) || 0;
            const total = Number(c.AdjustedTotalCoursesCount) || 0;
            const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
            return (
              <li key={`${c.UserId}-${c.ModuleId}`} className={`rmacr-card ${added ? 'is-added' : 'is-needed'}`}>
                <div className="rmacr-card-top">
                  <span className="rmacr-icon">
                    <FontAwesomeIcon icon={faAward} />
                  </span>
                  <div className="rmacr-who">
                    <strong>
                      {c.FirstName} {c.LastName}
                    </strong>
                    <span>{c.ModuleName}</span>
                  </div>
                  <span className="rmacr-pill">
                    <FontAwesomeIcon icon={added ? faCircleCheck : faCircleExclamation} /> {added ? 'Added' : 'Needed'}
                  </span>
                </div>
                <div className="rmacr-progress">
                  <span className="rmacr-bar" aria-hidden="true">
                    <i style={{ width: `${pct}%` }} />
                  </span>
                  <small>
                    {done}/{total} classes
                  </small>
                </div>
                <div className="rmacr-actions">
                  <a
                    className="rmacr-profile"
                    href={`/admin/StudentProfile/${c.UserId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${c.FirstName} ${c.LastName}'s profile`}
                    title="Student profile"
                  >
                    <FontAwesomeIcon icon={faUser} />
                  </a>
                  <a className="rmacr-open" href={`/admin/StudentProfile/${c.UserId}?tab=certificates`} target="_blank" rel="noopener noreferrer">
                    {added ? 'View certificates' : 'Add certificate'} <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  </a>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Certificates;